import { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../../constants/featureFlags';
import VisitCompletionByClinician from './VisitCompletionByClinician';
import VisitCompletionByClinicianAndBranch from './VisitCompletionByClinicianAndBranch';

export interface AdoptionReportRef {
  exportToCSV: () => void;
}

const AdoptionReport = forwardRef<AdoptionReportRef>((_props, ref) => {
  const { hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );

  const hasBranchReportFlag = hasFeatureFlag(
    FeatureFlag.ADOPTION_REPORT_BY_BRANCH
  );

  const [reportType, setReportType] = useState<'clinician' | 'branch'>(
    'clinician'
  );
  const [exportHandler, setExportHandler] = useState<(() => void) | null>(null);

  // We don't really need refs to children if we hoist the export handler via callback
  // but keeping refs might be useful if we wanted to call methods directly.
  // However, the export button is now inside the sub-components in the original designs?
  // No, in Reporting.tsx the export button is in the header.
  // And in Upwork design, the export button was next to the selector.
  // In Reporting.tsx, the export button calls `adoptionReportRef.current?.exportToCSV()`.
  // So I need to expose `exportToCSV` which calls the current report's export function.

  const handleExportRegistry = (handler: (() => void) | null) => {
    setExportHandler(() => handler);
  };

  const exportToCSV = () => {
    if (exportHandler) {
      exportHandler();
    }
  };

  useImperativeHandle(ref, () => ({
    exportToCSV,
  }));

  return (
    <div className='space-y-4'>
      <div className='flex justify-between items-center'>
        {hasBranchReportFlag ? (
          <div className='w-[400px]'>
            <Select
              value={reportType}
              onValueChange={value =>
                setReportType(value as 'clinician' | 'branch')
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Select report type' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='clinician'>
                  Adoption Report - Visit Completion by Clinician
                </SelectItem>
                <SelectItem value='branch'>
                  Adoption Report - Visit Completion By Clinician And Branch
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : (
          <h2 className='text-lg font-semibold text-foreground'>
            Adoption Report - Visit Completion by Clinician
          </h2>
        )}
      </div>

      {reportType === 'branch' && hasBranchReportFlag ? (
        <VisitCompletionByClinicianAndBranch
          onExportRegistry={handleExportRegistry}
        />
      ) : (
        <VisitCompletionByClinician onExportRegistry={handleExportRegistry} />
      )}
    </div>
  );
});

AdoptionReport.displayName = 'AdoptionReport';

export default AdoptionReport;
